/**
 * Subscription Payment Result Pages
 * Shows success or cancel status after PayHere payment
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle2, XCircle, Loader2, ArrowRight, Home } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Success Page
export const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  
  const subscriptionId = searchParams.get('subscription_id');

  useEffect(() => {
    const fetchStatus = async () => {
      if (!subscriptionId) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/api/payments/payhere/status/${subscriptionId}`);
        if (response.data.success) {
          setSubscription(response.data);
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      } finally {
        setLoading(false);
      }
    };

    // Delay to allow webhook to process
    setTimeout(fetchStatus, 2000);
  }, [subscriptionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4" data-testid="subscription-success-page">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Payment Successful!
          </h1>
          
          <p className="text-gray-600 mb-6">
            Thank you for subscribing. Your subscription is now active.
          </p>
          
          {subscription && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <div className="flex justify-between mb-2">
                <span className="text-gray-500">Subscription ID</span>
                <span className="font-mono text-sm">{subscriptionId}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-500">Amount</span>
                <span className="font-semibold">LKR {subscription.amount?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className="text-green-600 font-semibold capitalize">{subscription.status}</span>
              </div>
            </div>
          )}
          
          <div className="space-y-3">
            <Button className="w-full" onClick={() => navigate('/jobs/dashboard')}>
              Go to Dashboard
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button variant="outline" className="w-full" onClick={() => navigate('/jobs')}>
              <Home className="h-4 w-4 mr-2" />
              Browse Jobs
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Cancel Page
export const SubscriptionCancel = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subscriptionId = searchParams.get('subscription_id');

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4" data-testid="subscription-cancel-page">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="h-10 w-10 text-red-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Payment Cancelled
          </h1>
          
          <p className="text-gray-600 mb-6">
            Your payment was cancelled. No charges have been made to your account.
          </p>
          
          <div className="space-y-3">
            <Button className="w-full" onClick={() => navigate('/jobs/subscription')}>
              Try Again
            </Button>
            <Button variant="outline" className="w-full" onClick={() => navigate('/jobs')}>
              <Home className="h-4 w-4 mr-2" />
              Back to Jobs
            </Button>
          </div>
          
          <p className="text-sm text-gray-500 mt-6">
            Need help? <Link to="/contact" className="text-blue-600 hover:underline">Contact support</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default { SubscriptionSuccess, SubscriptionCancel };
